import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams, useNavigate } from 'react-router-dom';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Slider } from '../components/ui/slider';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { Checkbox } from '../components/ui/checkbox';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/utils';
import { 
  Image as ImageIcon, Video, Upload, Camera, X, Search, Filter, 
  MoreVertical, Trash2, Edit2, Share2, MessageSquare, Send,
  Play, Pause, Volume2, VolumeX, Maximize2, Download,
  Mail, Link2, Users, ExternalLink, Copy, Check,
  ChevronLeft, ChevronRight, Loader2, Plus, Calendar, Clock,
  Horse, FileImage, Tag, RefreshCw, Circle, RotateCw, RotateCcw, Crop, User, ZoomIn, Star, ArrowLeft
} from 'lucide-react';
import axios from 'axios';

const resolveUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${API_URL}${url}`;
};

const addCacheBust = (url, ts) => {
  if (!ts || !url) return url || '';
  return url.includes('?') ? `${url}&t=${ts}` : `${url}?t=${ts}`;
};

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Types de médias
const mediaTypes = [
  { value: 'all', label: 'Tous', icon: null },
  { value: 'image', label: 'Images', icon: ImageIcon },
  { value: 'video', label: 'Vidéos', icon: Video },
];

// Jours de la semaine en français
const DAYS_FR = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
const MONTHS_FR = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];

// Format date for activity display
const formatActivityDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const day = DAYS_FR[date.getDay()];
  const dayNum = date.getDate();
  const month = MONTHS_FR[date.getMonth()];
  const hours = date.getHours().toString().padStart(2, '0');
  const mins = date.getMinutes().toString().padStart(2, '0');
  return `${day} ${dayNum} ${month} • ${hours}:${mins}`;
};

export const GalleryPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [medias, setMedias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [mediaToEdit, setMediaToEdit] = useState(null);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [mediaToShare, setMediaToShare] = useState(null);
  const [stats, setStats] = useState(null);
  const [horses, setHorses] = useState([]);
  const [activities, setActivities] = useState([]);
  // Viewer and selection
  const [viewerIndex, setViewerIndex] = useState(null);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  // Zoom level for gallery grid (default 4 columns on mobile)
  const [gridColumns, setGridColumns] = useState(4);
  // Tag filter from URL
  const [tagFilter, setTagFilter] = useState(searchParams.get('tag') || '');
  
  // Ref for gallery grid (keeping for potential future use)
  const galleryRef = useRef(null);

  // Viewer inline editing
  const [viewerRotation, setViewerRotation] = useState(0);
  const [viewerCropMode, setViewerCropMode] = useState(false);
  const [viewerCrop, setViewerCrop] = useState(undefined);
  const [viewerCompletedCrop, setViewerCompletedCrop] = useState(null);
  const [viewerPendingCrop, setViewerPendingCrop] = useState(null);
  const [viewerApplying, setViewerApplying] = useState(false);
  const [viewerCacheBust, setViewerCacheBust] = useState(0);
  const viewerImgRef = useRef(null);

  useEffect(() => {
    fetchMedias();
    fetchStats();
    fetchHorses();
    fetchActivities();
  }, []);
  
  // Update tag filter from URL
  useEffect(() => {
    const tag = searchParams.get('tag');
    setTagFilter(tag || '');
  }, [searchParams]);
  
  // Polling effect to update media status for processing items
  useEffect(() => {
    const processingMedias = medias.filter(m => 
      m.status === 'uploading' || m.status === 'processing'
    );
    
    if (processingMedias.length === 0) return;
    
    const pollInterval = setInterval(async () => {
      let updated = false;
      const updatedMedias = [...medias];
      
      for (const media of processingMedias) {
        try {
          const res = await axios.get(`${API_URL}/api/gallery/status/${media.id}`);
          const idx = updatedMedias.findIndex(m => m.id === media.id);
          
          if (idx !== -1 && res.data.status !== updatedMedias[idx].status) {
            updatedMedias[idx] = {
              ...updatedMedias[idx],
              status: res.data.status,
              processing_progress: res.data.progress,
              variants: res.data.variants
            };
            updated = true;
          }
        } catch (err) {
          console.error(`Error polling status for ${media.id}:`, err);
        }
      }
      
      if (updated) {
        setMedias(updatedMedias);
      }
    }, 2000); // Poll every 2 seconds
    
    return () => clearInterval(pollInterval);
  }, [medias]);

  const fetchMedias = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      params.append('sort', 'date_desc'); // Sort by date descending
      
      const queryString = params.toString();
      const url = queryString 
        ? `${API_URL}/api/gallery/?${queryString}`
        : `${API_URL}/api/gallery/`;
      
      const res = await axios.get(url);
      // Sort by upload date descending (most recent first)
      const sortedMedias = res.data.sort((a, b) => 
        new Date(b.created_at || b.upload_date) - new Date(a.created_at || a.upload_date)
      );
      setMedias(sortedMedias);
    } catch (error) {
      console.error('Error fetching media:', error);
      toast.error('Erreur lors du chargement de la galerie');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/gallery/stats/overview`);
      setStats(res.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchHorses = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/horses`);
      setHorses(res.data);
    } catch (error) {
      console.error('Error fetching horses:', error);
    }
  };

  const fetchActivities = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/gallery/activities?limit=50`);
      setActivities(res.data);
    } catch (error) {
      console.error('Error fetching activities:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchMedias();
  };

  const handleDeleteMedia = async (mediaId) => {
    if (!window.confirm('Supprimer ce média ?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/gallery/${mediaId}`);
      toast.success('Média supprimé');
      setSelectedMedia(null);
      fetchMedias();
      fetchStats();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur lors de la suppression'));
    }
  };

  const openShareModal = (media) => {
    setMediaToShare(media);
    setShareModalOpen(true);
  };

  const openEditModal = (media) => {
    setMediaToEdit(media);
    setEditModalOpen(true);
  };

  // Viewer inline editing handlers
  const viewerHandleRotate = (dir) => {
    const delta = dir === 'cw' ? 90 : -90;
    setViewerRotation(prev => (prev + delta + 360) % 360);
  };

  const viewerHandleCropConfirm = () => {
    if (viewerCompletedCrop && viewerImgRef.current &&
        viewerCompletedCrop.width > 5 && viewerCompletedCrop.height > 5) {
      const img = viewerImgRef.current;
      const scaleX = img.naturalWidth / (img.width || 1);
      const scaleY = img.naturalHeight / (img.height || 1);
      const imgW = img.naturalWidth;
      const imgH = img.naturalHeight;
      const x = Math.max(0, Math.round(viewerCompletedCrop.x * scaleX));
      const y = Math.max(0, Math.round(viewerCompletedCrop.y * scaleY));
      const w = Math.min(imgW - x, Math.round(viewerCompletedCrop.width * scaleX));
      const h = Math.min(imgH - y, Math.round(viewerCompletedCrop.height * scaleY));
      if (w > 5 && h > 5) setViewerPendingCrop({ x, y, width: w, height: h });
    }
    setViewerCropMode(false);
    setViewerCrop(undefined);
    setViewerCompletedCrop(null);
  };

  const viewerHandleCropCancel = () => {
    setViewerCropMode(false);
    setViewerCrop(undefined);
    setViewerCompletedCrop(null);
  };

  const viewerHandleCancel = () => {
    setViewerRotation(0);
    setViewerPendingCrop(null);
    setViewerCropMode(false);
    setViewerCrop(undefined);
    setViewerCompletedCrop(null);
  };

  const viewerHandleApply = async () => {
    const media = filteredMedias[viewerIndex];
    if (!media) return;
    setViewerApplying(true);
    try {
      if (viewerRotation !== 0) {
        await axios.post(`${API_URL}/api/gallery/${media.id}/rotate`, { angle: viewerRotation });
      }
      if (viewerPendingCrop) {
        await axios.post(`${API_URL}/api/gallery/${media.id}/crop`, viewerPendingCrop);
      }
      toast.success('Modifications appliquées');
      await fetchMedias();
      setViewerCacheBust(Date.now());
      setViewerRotation(0);
      setViewerPendingCrop(null);
    } catch (error) {
      toast.error("Erreur lors de l'application");
    } finally {
      setViewerApplying(false);
    }
  };

  // Filter medias based on search term
  const filteredMedias = medias.filter(m => {
    // Filter by tag first (from URL params)
    if (tagFilter) {
      const hasTag = m.custom_tags?.includes(tagFilter);
      if (!hasTag) return false;
    }
    
    // Then filter by search term
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      m.title?.toLowerCase().includes(term) ||
      m.description?.toLowerCase().includes(term) ||
      m.horses?.some(h => h.nom?.toLowerCase().includes(term)) ||
      m.activities?.some(a => a.type_cours?.toLowerCase().includes(term)) ||
      m.custom_tags?.some(t => t.toLowerCase().includes(term))
    );
  });
  
  // Clear tag filter
  const clearTagFilter = () => {
    setTagFilter('');
    setSearchParams({});
  };

  // Selection functions
  const toggleSelection = (mediaId) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(mediaId)) {
      newSelected.delete(mediaId);
    } else {
      newSelected.add(mediaId);
    }
    setSelectedIds(newSelected);
  };

  const selectAll = () => {
    setSelectedIds(new Set(filteredMedias.map(m => m.id)));
  };

  const deselectAll = () => {
    setSelectedIds(new Set());
    setSelectionMode(false);
  };

  const shareSelected = () => {
    const selectedMedias = filteredMedias.filter(m => selectedIds.has(m.id));
    if (selectedMedias.length === 1) {
      openShareModal(selectedMedias[0]);
    } else if (selectedMedias.length > 1) {
      // For multiple, we'll share the first one for now (could implement batch sharing later)
      toast.info(`${selectedMedias.length} médias sélectionnés`);
      openShareModal(selectedMedias[0]);
    }
    setSelectionMode(false);
    setSelectedIds(new Set());
  };

  // Viewer navigation
  const openViewer = (index) => {
    setViewerIndex(index);
  };

  const closeViewer = () => {
    setViewerIndex(null);
    setViewerRotation(0);
    setViewerCropMode(false);
    setViewerCrop(undefined);
    setViewerCompletedCrop(null);
    setViewerPendingCrop(null);
    setViewerApplying(false);
  };

  const goToPrevious = () => {
    if (viewerIndex > 0) {
      setViewerIndex(viewerIndex - 1);
    }
  };

  const goToNext = () => {
    if (viewerIndex < filteredMedias.length - 1) {
      setViewerIndex(viewerIndex + 1);
    }
  };

  if (loading && medias.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#2E4A3D]" />
      </div>
    );
  }

  return (
    <div className="animate-fadeIn h-full flex flex-col" data-testid="gallery-page">
      {/* Fullscreen Viewer */}
      {viewerIndex !== null && filteredMedias[viewerIndex] && createPortal(
        <div
          className="fixed inset-0 bg-black flex flex-col"
          style={{ zIndex: 99999 }}
        >
          {/* Header */}
          <div
            className="flex items-center justify-between px-4 py-3"
            style={{ paddingTop: 'max(env(safe-area-inset-top, 0px), 0.75rem)' }}
          >
            <button
              type="button"
              onClick={viewerCropMode ? viewerHandleCropCancel : closeViewer}
              className="text-white px-3 py-2 text-sm font-medium"
              disabled={viewerApplying}
            >
              {viewerCropMode ? 'Annuler' : <X className="w-6 h-6" />}
            </button>
            <span className="text-white text-sm font-medium">
              {viewerCropMode ? 'Recadrer' : `${viewerIndex + 1} / ${filteredMedias.length}`}
            </span>
            {viewerCropMode ? (
              <button
                type="button"
                onClick={viewerHandleCropConfirm}
                disabled={!viewerCompletedCrop}
                className="text-white px-3 py-2 text-sm font-medium disabled:opacity-40"
              >
                OK
              </button>
            ) : (
              <div className="w-12" />
            )}
          </div>

          {/* Media display */}
          <div className="flex-1 flex items-center justify-center overflow-hidden relative">
            {viewerCropMode ? (
              <ReactCrop
                crop={viewerCrop}
                onChange={(c) => setViewerCrop(c)}
                onComplete={(c) => setViewerCompletedCrop(c)}
              >
                <img
                  ref={viewerImgRef}
                  src={addCacheBust(resolveUrl(filteredMedias[viewerIndex].file_url), viewerCacheBust)}
                  alt=""
                  className="max-w-full max-h-[80vh] object-contain block"
                />
              </ReactCrop>
            ) : filteredMedias[viewerIndex].media_type === 'image' ? (
              <img
                src={addCacheBust(resolveUrl(filteredMedias[viewerIndex].file_url), viewerCacheBust)}
                alt={filteredMedias[viewerIndex].title || ''}
                className="max-w-full max-h-full object-contain transition-transform duration-300"
                style={{ transform: `rotate(${viewerRotation}deg)` }}
              />
            ) : (
              <video
                src={resolveUrl(filteredMedias[viewerIndex].file_url)}
                className="max-w-full max-h-full object-contain"
                controls
                autoPlay
                playsInline
              />
            )}

            {/* Prev/Next - hidden in crop mode */}
            {!viewerCropMode && viewerIndex > 0 && (
              <button
                type="button"
                onClick={goToPrevious}
                className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 flex items-center justify-center"
              >
                <ChevronLeft className="w-6 h-6 text-white" />
              </button>
            )}
            {!viewerCropMode && viewerIndex < filteredMedias.length - 1 && (
              <button
                type="button"
                onClick={goToNext}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 flex items-center justify-center"
              >
                <ChevronRight className="w-6 h-6 text-white" />
              </button>
            )}
          </div>

          {/* Bottom bar */}
          {!viewerCropMode && (
            <div
              className="bg-black/80 px-4 py-3"
              style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 0.75rem)' }}
            >
              {/* Pending changes indicator + Valider/Annuler */}
              {(viewerRotation !== 0 || viewerPendingCrop) ? (
                <div className="flex items-center justify-between">
                  <span className="text-amber-400 text-xs">
                    {viewerRotation !== 0 && `${viewerRotation}° `}
                    {viewerPendingCrop && '✓ Recadré'}
                  </span>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={viewerHandleCancel}
                      disabled={viewerApplying}
                      className="flex items-center gap-1.5 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-xl text-white text-sm font-medium disabled:opacity-40"
                    >
                      <X className="w-4 h-4" />
                      Annuler
                    </button>
                    <button
                      type="button"
                      onClick={viewerHandleApply}
                      disabled={viewerApplying}
                      className="flex items-center gap-1.5 px-4 py-2 bg-green-500 hover:bg-green-600 rounded-xl text-white text-sm font-medium disabled:opacity-40"
                    >
                      {viewerApplying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                      Appliquer
                    </button>
                  </div>
                </div>
              ) : (
                /* Normal action buttons */
                <div className="flex justify-center gap-2">
                  <button
                    type="button"
                    onClick={() => { openEditModal(filteredMedias[viewerIndex]); closeViewer(); }}
                    className="w-14 h-14 flex flex-col items-center justify-center text-white hover:bg-white/20 rounded-lg transition-colors"
                  >
                    <Edit2 className="w-5 h-5" />
                    <span className="text-[9px] mt-0.5">Éditer</span>
                  </button>
                  {filteredMedias[viewerIndex]?.media_type === 'image' && (
                    <>
                      <button
                        type="button"
                        onClick={() => viewerHandleRotate('ccw')}
                        className="w-14 h-14 flex flex-col items-center justify-center text-white hover:bg-white/20 rounded-lg transition-colors"
                      >
                        <RotateCcw className="w-5 h-5" />
                        <span className="text-[9px] mt-0.5">Gauche</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => viewerHandleRotate('cw')}
                        className="w-14 h-14 flex flex-col items-center justify-center text-white hover:bg-white/20 rounded-lg transition-colors"
                      >
                        <RotateCw className="w-5 h-5" />
                        <span className="text-[9px] mt-0.5">Droite</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setViewerCropMode(true)}
                        className="w-14 h-14 flex flex-col items-center justify-center text-white hover:bg-white/20 rounded-lg transition-colors"
                      >
                        <Crop className="w-5 h-5" />
                        <span className="text-[9px] mt-0.5">Recadrer</span>
                      </button>
                    </>
                  )}
                  <button
                    type="button"
                    onClick={() => { openShareModal(filteredMedias[viewerIndex]); closeViewer(); }}
                    className="w-14 h-14 flex flex-col items-center justify-center text-blue-400 hover:bg-blue-500/20 rounded-lg transition-colors"
                  >
                    <Share2 className="w-5 h-5" />
                    <span className="text-[9px] mt-0.5">Partager</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => { handleDeleteMedia(filteredMedias[viewerIndex].id); closeViewer(); }}
                    className="w-14 h-14 flex flex-col items-center justify-center text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-5 h-5" />
                    <span className="text-[9px] mt-0.5">Supprimer</span>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>,
        document.body
      )}

      {/* Compact Header - Mobile First */}
      <div className="flex items-center gap-2 mb-2 px-1">
        {/* Search field */}
        <div className="flex-1 relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <Input
            placeholder="Rechercher..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8 h-9 text-sm bg-stone-50 border-stone-200"
            data-testid="search-media"
          />
        </div>
        
        {/* Zoom slider - minimal */}
        <input
          type="range"
          min="2"
          max="6"
          value={gridColumns}
          onChange={(e) => setGridColumns(parseInt(e.target.value))}
          className="w-12 h-1 appearance-none bg-stone-200 rounded-full cursor-pointer accent-[#2E4A3D]"
          style={{ 
            WebkitAppearance: 'none',
            background: `linear-gradient(to right, #2E4A3D 0%, #2E4A3D ${((gridColumns - 2) / 4) * 100}%, #d6d3d1 ${((gridColumns - 2) / 4) * 100}%, #d6d3d1 100%)`
          }}
          data-testid="gallery-zoom-slider"
        />
        
        {/* Add button - compact */}
        <button
          type="button"
          onClick={() => setUploadModalOpen(true)}
          className="w-9 h-9 rounded-full bg-[#2E4A3D] text-white flex items-center justify-center shadow-sm active:scale-95"
          data-testid="upload-media-btn"
        >
          <Plus className="w-5 h-5" />
        </button>
      </div>
      
      {/* Tag filter badge */}
      {tagFilter && (
        <div className="flex items-center gap-2 mb-2 px-1">
          {/* Back button to return to photo modal */}
          {tagFilter === 'photo_du_jour' && (
            <button
              type="button"
              onClick={() => navigate('/dashboard?openPhotoModal=true')}
              className="w-8 h-8 rounded-full bg-amber-100 hover:bg-amber-200 text-amber-700 flex items-center justify-center transition-colors"
              title="Retour à l'ajout de photos"
              data-testid="back-to-photo-modal"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
          )}
          <Badge 
            variant="secondary" 
            className="bg-amber-100 text-amber-800 hover:bg-amber-200 cursor-pointer flex items-center gap-1"
            onClick={clearTagFilter}
          >
            <Star className="w-3 h-3" />
            {tagFilter === 'photo_du_jour' ? 'Photos du jour' : tagFilter}
            <X className="w-3 h-3 ml-1" />
          </Badge>
          <span className="text-xs text-stone-500">
            {filteredMedias.length} média(s)
          </span>
        </div>
      )}

      {/* Selection mode bar */}
      {selectionMode && (
        <div className="flex items-center gap-2 mb-2 px-1 py-1.5 bg-stone-100 rounded-lg">
          <span className="text-xs text-stone-600 flex-1">
            {selectedIds.size} sélectionné(s)
          </span>
          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={selectAll}>
            Tout
          </Button>
          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={deselectAll}>
            Annuler
          </Button>
          {selectedIds.size > 0 && (
            <Button size="sm" className="h-7 text-xs bg-[#2E4A3D]" onClick={shareSelected}>
              <Share2 className="w-3 h-3 mr-1" />
              Partager
            </Button>
          )}
        </div>
      )}

      {/* Gallery Grid - Mobile First Mosaic */}
      <div 
        ref={galleryRef}
        className="flex-1 overflow-y-auto -mx-2 px-0.5"
      >
        {filteredMedias.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <ImageIcon className="w-12 h-12 text-stone-300 mb-3" />
            <p className="text-sm text-stone-500">Aucun média</p>
            <button
              type="button"
              onClick={() => setUploadModalOpen(true)}
              className="mt-3 text-sm text-[#2E4A3D] font-medium"
            >
              + Ajouter des médias
            </button>
          </div>
        ) : (
          <div 
            className="grid gap-0.5"
            style={{ 
              gridTemplateColumns: `repeat(${gridColumns}, minmax(0, 1fr))` 
            }}
          >
            {filteredMedias.map((media, index) => (
              <MediaCard 
                key={media.id} 
                media={media} 
                onClick={() => {
                  if (selectionMode) {
                    toggleSelection(media.id);
                  } else {
                    openViewer(index);
                  }
                }}
                selectionMode={selectionMode}
                isSelected={selectedIds.has(media.id)}
                onToggleSelect={() => toggleSelection(media.id)}
              />
            ))}
        </div>
      )}
      </div>

      {/* Media Detail Modal - removed, using inline viewer */}

      {/* Upload Modal */}
      <UploadMediaModal
        open={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        onSuccess={() => {
          setUploadModalOpen(false);
          fetchMedias();
          fetchStats();
        }}
      />

      {/* Edit Modal */}
      {mediaToEdit && (
        <EditMediaModal
          open={editModalOpen}
          onClose={() => {
            setEditModalOpen(false);
            setMediaToEdit(null);
          }}
          media={mediaToEdit}
          horses={horses}
          activities={activities}
          onSuccess={() => {
            setEditModalOpen(false);
            setMediaToEdit(null);
            fetchMedias();
          }}
        />
      )}

      {/* Share Modal */}
      {mediaToShare && (
        <ShareMediaModal
          open={shareModalOpen}
          onClose={() => {
            setShareModalOpen(false);
            setMediaToShare(null);
          }}
          media={mediaToShare}
        />
      )}

    </div>
  );
};

// ============ Media Card Component - Optimized with Variants ============
const MediaCard = ({ media, onClick, selectionMode, isSelected, onToggleSelect }) => {
  const isVideo = media.media_type === 'video';
  const [imageLoaded, setImageLoaded] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  
  // Check processing status
  const isProcessing = media.status === 'uploading' || media.status === 'processing';
  const hasError = media.status === 'error';
  
  // Get optimized URL - prefer thumb variant, fallback to thumbnail_url, then file_url
  const getOptimizedImageUrl = () => {
    if (media.variants?.thumb?.url) {
      return resolveUrl(media.variants.thumb.url);
    }
    if (media.thumbnail_url) {
      return resolveUrl(media.thumbnail_url);
    }
    if (media.file_url) {
      return resolveUrl(media.file_url);
    }
    return null;
  };
  
  const imageUrl = !isVideo ? getOptimizedImageUrl() : null;
  const videoUrl = isVideo ? resolveUrl(media.file_url) : null;
  
  // Placeholder background using dominant_color
  const placeholderStyle = {
    backgroundColor: media.dominant_color || '#e7e5e4'
  };
  
  return (
    <div 
      className={`aspect-square relative cursor-pointer overflow-hidden ${
        isSelected ? 'ring-2 ring-[#2E4A3D] ring-inset' : ''
      }`}
      style={placeholderStyle}
      onClick={onClick}
      data-testid={`media-card-${media.id}`}
    >
      {/* Blur placeholder while loading */}
      {media.blur_placeholder && !imageLoaded && !isVideo && (
        <img 
          src={media.blur_placeholder}
          alt=""
          className="absolute inset-0 w-full h-full object-cover scale-110 blur-sm"
        />
      )}
      
      {/* Processing indicator */}
      {isProcessing && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/20 z-10">
          <Loader2 className="w-6 h-6 text-white animate-spin" />
          <span className="text-xs text-white mt-1">
            {media.processing_progress ? `${media.processing_progress}%` : 'Traitement...'}
          </span>
        </div>
      )}
      
      {/* Error indicator */}
      {hasError && (
        <div className="absolute inset-0 flex items-center justify-center bg-red-500/20 z-10">
          <X className="w-6 h-6 text-red-500" />
        </div>
      )}
      
      {/* Image thumbnail */}
      {imageUrl && !imageError && !isProcessing ? (
        <img 
          src={imageUrl}
          alt=""
          className={`w-full h-full object-cover transition-opacity duration-300 ${
            imageLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          loading="lazy"
          onLoad={() => setImageLoaded(true)}
          onError={() => setImageError(true)}
        />
      ) : isVideo && videoUrl && !isProcessing ? (
        // Video thumbnail - uses video element with poster frame
        <div className="relative w-full h-full">
          <video 
            src={`${videoUrl}#t=0.5`}
            className="w-full h-full object-cover"
            muted
            playsInline
            preload="metadata"
            onLoadedData={() => setVideoLoaded(true)}
          />
          {!videoLoaded && (
            <div className="absolute inset-0 flex items-center justify-center">
              <Video className="w-8 h-8 text-stone-400" />
            </div>
          )}
        </div>
      ) : !isProcessing && !hasError && (
        // Fallback placeholder
        <div className="w-full h-full flex items-center justify-center">
          <ImageIcon className="w-8 h-8 text-stone-400" />
        </div>
      )}
      
      {/* Video play indicator - small and discrete */}
      {isVideo && !isProcessing && (
        <div className="absolute bottom-1 left-1">
          <Play className="w-4 h-4 text-white drop-shadow-md fill-white" />
        </div>
      )}
      
      {/* Selection checkbox */}
      {selectionMode && (
        <div 
          className="absolute top-1 right-1 z-10"
          onClick={(e) => {
            e.stopPropagation();
            onToggleSelect();
          }}
        >
          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shadow-sm ${
            isSelected 
              ? 'bg-[#2E4A3D] border-[#2E4A3D]' 
              : 'bg-white/80 border-white/80'
          }`}>
            {isSelected && <Check className="w-3 h-3 text-white" />}
          </div>
        </div>
      )}
    </div>
  );
};

// ============ Media Detail Modal ============
const MediaDetailModal = ({ media, onClose, onShare, onDelete, onUpdate }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [comments, setComments] = useState(media.comments || []);
  const [loadingOriginal, setLoadingOriginal] = useState(false);
  const [showOriginal, setShowOriginal] = useState(false);
  const videoRef = useRef(null);
  
  const isVideo = media.media_type === 'video';
  
  // Get optimized URL for viewer - prefer screen variant, fallback to file_url
  const getScreenUrl = () => {
    if (media.variants?.screen?.url) {
      return resolveUrl(media.variants.screen.url);
    }
    return resolveUrl(media.file_url);
  };
  
  const getOriginalUrl = () => {
    if (media.original_url) {
      return resolveUrl(media.original_url);
    }
    return resolveUrl(media.file_url);
  };
  
  // Use screen by default, original when zooming
  const mediaUrl = showOriginal ? getOriginalUrl() : getScreenUrl();
  
  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };
  
  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };
  
  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    
    setSubmittingComment(true);
    try {
      const res = await axios.post(`${API_URL}/api/gallery/${media.id}/comments`, {
        content: newComment.trim()
      });
      setComments([...comments, res.data]);
      setNewComment('');
      toast.success('Commentaire ajouté');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur'));
    } finally {
      setSubmittingComment(false);
    }
  };
  
  const handleDeleteComment = async (commentId) => {
    try {
      await axios.delete(`${API_URL}/api/gallery/${media.id}/comments/${commentId}`);
      setComments(comments.filter(c => c.id !== commentId));
      toast.success('Commentaire supprimé');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur'));
    }
  };
  
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] max-w-4xl max-h-[90vh] overflow-hidden p-0" aria-describedby="media-detail-description">
        <DialogHeader className="sr-only">
          <DialogTitle>{media.title}</DialogTitle>
        </DialogHeader>
        <span id="media-detail-description" className="sr-only">Détails du média {media.title}</span>
        
        <div className="flex flex-col lg:flex-row h-full max-h-[90vh]">
          {/* Media viewer */}
          <div className="flex-1 bg-black relative min-h-[300px] lg:min-h-0 flex items-center justify-center">
            {isVideo ? (
              <div className="relative w-full h-full">
                <video
                  ref={videoRef}
                  src={mediaUrl}
                  className="w-full h-full object-contain"
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                  onClick={togglePlay}
                />
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                  <Button 
                    variant="secondary" 
                    size="sm" 
                    onClick={togglePlay}
                    className="bg-white/90"
                  >
                    {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                  </Button>
                  <Button 
                    variant="secondary" 
                    size="sm" 
                    onClick={toggleMute}
                    className="bg-white/90"
                  >
                    {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="relative">
                <img 
                  src={mediaUrl}
                  alt={media.title}
                  className="max-w-full max-h-full object-contain"
                />
                {/* Button to load original quality */}
                {!showOriginal && media.variants?.screen && (
                  <button
                    onClick={() => {
                      setLoadingOriginal(true);
                      setShowOriginal(true);
                    }}
                    className="absolute bottom-4 right-4 flex items-center gap-1.5 px-3 py-1.5 bg-black/60 hover:bg-black/80 text-white text-xs rounded-full transition-colors"
                    title="Voir en qualité originale"
                  >
                    <ZoomIn className="w-3.5 h-3.5" />
                    <span>HD</span>
                  </button>
                )}
                {loadingOriginal && !showOriginal && (
                  <div className="absolute bottom-4 right-4 flex items-center gap-1.5 px-3 py-1.5 bg-black/60 text-white text-xs rounded-full">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    <span>Chargement...</span>
                  </div>
                )}
                {showOriginal && (
                  <div className="absolute bottom-4 right-4 flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600/80 text-white text-xs rounded-full">
                    <Check className="w-3.5 h-3.5" />
                    <span>Original</span>
                  </div>
                )}
              </div>
            )}
            
            <button 
              onClick={onClose}
              className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors lg:hidden"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Sidebar */}
          <div className="w-full lg:w-80 flex flex-col bg-white overflow-hidden">
            {/* Header */}
            <div className="p-4 border-b">
              <div className="flex justify-between items-start">
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-[#2E4A3D] truncate">{media.title}</h3>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                      <MoreVertical className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={onShare}>
                      <Share2 className="w-4 h-4 mr-2" />
                      Partager
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <a href={mediaUrl} download target="_blank" rel="noopener noreferrer">
                        <Download className="w-4 h-4 mr-2" />
                        Télécharger
                      </a>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={onDelete} className="text-red-600">
                      <Trash2 className="w-4 h-4 mr-2" />
                      Supprimer
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              
              {media.description && (
                <p className="text-sm text-stone-600 mt-2">{media.description}</p>
              )}
              
              {/* Tags */}
              <div className="mt-3 space-y-2">
                {media.horses?.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {media.horses.map(h => (
                      <Badge key={h.id} variant="outline" className="text-xs">
                        🐴 {h.nom}
                      </Badge>
                    ))}
                  </div>
                )}
                {media.activities?.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {media.activities.map(a => (
                      <Badge 
                        key={a.id} 
                        variant="outline" 
                        className="text-xs"
                        style={{ borderColor: a.color || '#2E4A3D' }}
                      >
                        📅 {a.type_cours} - {formatActivityDate(a.date_heure_debut)}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="flex gap-2 mt-3 text-xs text-stone-500">
                <span>Par {media.uploaded_by_name}</span>
                <span>•</span>
                <span>{new Date(media.created_at).toLocaleDateString('fr-FR')}</span>
              </div>
            </div>
            
            {/* Comments */}
            <div className="flex-1 overflow-y-auto p-4">
              <h4 className="font-medium text-sm mb-3 flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />
                Commentaires ({comments.length})
              </h4>
              
              {comments.length === 0 ? (
                <p className="text-sm text-stone-400 text-center py-4">Aucun commentaire</p>
              ) : (
                <div className="space-y-3">
                  {comments.map(comment => (
                    <div key={comment.id} className="p-3 bg-stone-50 rounded-lg group">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-sm text-stone-800">{comment.author_name}</p>
                          <p className="text-xs text-stone-500">
                            {new Date(comment.created_at).toLocaleDateString('fr-FR')}
                          </p>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => handleDeleteComment(comment.id)}
                        >
                          <Trash2 className="w-3 h-3 text-red-500" />
                        </Button>
                      </div>
                      <p className="text-sm text-stone-700 mt-1">{comment.content}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Add comment */}
            <div className="p-4 border-t">
              <div className="flex gap-2">
                <Input
                  placeholder="Ajouter un commentaire..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddComment()}
                  className="flex-1"
                  data-testid="comment-input"
                />
                <Button 
                  size="sm" 
                  onClick={handleAddComment}
                  disabled={!newComment.trim() || submittingComment}
                  className="bg-[#2E4A3D] hover:bg-[#1e3228]"
                  data-testid="send-comment-btn"
                >
                  {submittingComment ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ============ Upload Media Modal ============
const UploadMediaModal = ({ open, onClose, onSuccess, context }) => {
  // Context can be: 
  // { type: 'activity', activityId, cavalierIds, instructorId } - from Planning
  // { type: 'horse', horseId } - from Horse profile  
  // { type: 'cavalier', cavalierId } - from Cavalier profile
  // { type: 'gallery' } or undefined - from Gallery (no auto-tags)
  
  // Files are stored as objects with unique IDs to prevent iOS selection issues
  // Format: { id: string, file: File, previewUrl: string }
  const [files, setFiles] = useState([]);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadedCount, setUploadedCount] = useState(0);
  const [useCamera, setUseCamera] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [cameraFlash, setCameraFlash] = useState(false);
  const [cameraMode, setCameraMode] = useState('photo'); // 'photo' or 'video'
  const [cameraZoom, setCameraZoom] = useState(1);
  const [zoomOptions, setZoomOptions] = useState([1]); // Dynamic zoom options based on camera capabilities
  const [facingMode, setFacingMode] = useState('environment'); // 'environment' or 'user'
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [previewIndex, setPreviewIndex] = useState(null); // Index of file being previewed, null = no preview
  const videoPreviewRef = useRef(null);
  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const recordingIntervalRef = useRef(null);
  const fileIdCounter = useRef(0);
  
  // Generate unique file ID
  const generateFileId = () => {
    fileIdCounter.current += 1;
    return `file_${Date.now()}_${fileIdCounter.current}`;
  };
  
  // Cleanup preview URLs on unmount
  useEffect(() => {
    return () => {
      files.forEach(f => {
        if (f.previewUrl) URL.revokeObjectURL(f.previewUrl);
      });
      stopCamera();
    };
  }, []);
  
  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, [open]);
  
  // Callback ref to assign stream when video element is mounted
  const setVideoRef = useCallback((videoElement) => {
    videoPreviewRef.current = videoElement;
    if (videoElement && cameraStream) {
      videoElement.srcObject = cameraStream;
      videoElement.play().catch(() => {});
    }
  }, [cameraStream]);
  
  const handleFilesChange = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length > 0) {
      // Create wrapped file objects with unique IDs and preview URLs
      const wrappedFiles = selectedFiles.map(file => ({
        id: generateFileId(),
        file: file,
        previewUrl: URL.createObjectURL(file)
      }));
      setFiles(prev => [...prev, ...wrappedFiles]);
      if (selectedFiles.length === 1 && !title) {
        setTitle(selectedFiles[0].name.split('.')[0]);
      }
    }
  };
  
  const removeFile = (fileId) => {
    setFiles(prev => {
      const fileToRemove = prev.find(f => f.id === fileId);
      if (fileToRemove?.previewUrl) {
        URL.revokeObjectURL(fileToRemove.previewUrl);
      }
      return prev.filter(f => f.id !== fileId);
    });
  };
  
  const startCamera = async (facing = 'environment') => {
    try {
      // Check if getUserMedia is supported
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        toast.error('Votre navigateur ne supporte pas l\'accès à la caméra');
        return;
      }
      
      // Stop existing stream if any
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
      
      // Use exact constraint for more reliable camera selection
      const constraints = {
        video: { 
          facingMode: { ideal: facing },
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        }, 
        audio: cameraMode === 'video'
      };
      
      console.log('Requesting camera with constraints:', constraints);
      
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia(constraints);
      } catch (e) {
        // Fallback without facingMode constraint
        console.log('Trying fallback without facingMode');
        stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: cameraMode === 'video'
        });
      }
      
      console.log('Camera stream obtained:', stream);
      setCameraStream(stream);
      setUseCamera(true);
      setFacingMode(facing);
      setCameraZoom(1);
      
      // Detect zoom capabilities and generate zoom options
      const videoTrack = stream.getVideoTracks()[0];
      if (videoTrack) {
        try {
          const capabilities = videoTrack.getCapabilities();
          console.log('Camera capabilities:', capabilities);
          
          if (capabilities.zoom) {
            const minZoom = capabilities.zoom.min || 1;
            const maxZoom = capabilities.zoom.max || 1;
            
            // Generate zoom options based on available range
            const options = [];
            
            // Add ultra-wide (0.5x) if min zoom < 1
            if (minZoom < 1) {
              options.push(0.5);
            }
            
            // Always add 1x
            options.push(1);
            
            // Add intermediate zooms based on max
            if (maxZoom >= 2) options.push(2);
            if (maxZoom >= 3) options.push(3);
            if (maxZoom >= 5) options.push(5);
            if (maxZoom >= 10) options.push(10);
            
            setZoomOptions(options);
            console.log('Zoom options available:', options, `(min: ${minZoom}, max: ${maxZoom})`);
          } else {
            // No native zoom, offer digital zoom options
            setZoomOptions([1, 2]);
            console.log('No native zoom, using digital zoom');
          }
        } catch (e) {
          console.log('Could not get capabilities:', e);
          setZoomOptions([1, 2]);
        }
      }
    } catch (error) {
      console.error('Camera error:', error);
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        toast.error('Accès à la caméra refusé. Autorisez l\'accès dans les paramètres de votre navigateur.');
      } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        toast.error('Aucune caméra détectée sur cet appareil.');
      } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
        toast.error('La caméra est déjà utilisée par une autre application.');
      } else if (error.name === 'OverconstrainedError') {
        // Try again without facingMode constraint
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: cameraMode === 'video' });
          setCameraStream(stream);
          setUseCamera(true);
          return;
        } catch (e) {
          toast.error('Impossible d\'accéder à la caméra.');
        }
      } else {
        toast.error('Impossible d\'accéder à la caméra. Vérifiez les permissions.');
      }
    }
  };
  
  const switchCamera = async () => {
    const newFacing = facingMode === 'environment' ? 'user' : 'environment';
    await startCamera(newFacing);
  };
  
  const applyZoom = async (zoom) => {
    if (!cameraStream) return;
    
    const videoTrack = cameraStream.getVideoTracks()[0];
    if (videoTrack) {
      try {
        const capabilities = videoTrack.getCapabilities();
        console.log('Camera capabilities:', capabilities);
        
        if (capabilities.zoom) {
          // Le zoom natif est supporté - utilise les vrais objectifs !
          const minZoom = capabilities.zoom.min || 1;
          const maxZoom = capabilities.zoom.max || 1;
          
          // Mapper nos valeurs de zoom aux capacités réelles
          // 0.5x -> minZoom, 1x -> 1, 2x -> 2, 3x -> min(3, maxZoom)
          let targetZoom;
          if (zoom === 0.5) {
            targetZoom = minZoom; // Ultra grand-angle
          } else {
            targetZoom = Math.min(zoom, maxZoom);
          }
          
          console.log(`Applying native zoom: ${targetZoom} (min: ${minZoom}, max: ${maxZoom})`);
          await videoTrack.applyConstraints({ advanced: [{ zoom: targetZoom }] });
          setCameraZoom(zoom);
          toast.success(`Zoom ${zoom}× appliqué`);
        } else {
          // Zoom natif non supporté, utiliser zoom numérique
          console.log('Native zoom not supported, using digital zoom');
          setCameraZoom(zoom);
        }
      } catch (e) {
        console.error('Zoom error:', e);
        // Fallback to digital zoom
        setCameraZoom(zoom);
      }
    }
  };
  
  const stopCamera = () => {
    if (isRecording) {
      stopRecording();
    }
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setUseCamera(false);
    setCameraFlash(false);
    setCameraMode('photo');
    setCameraZoom(1);
    setRecordingTime(0);
  };
  
  // Video recording functions
  const startRecording = () => {
    if (!cameraStream) return;
    
    recordedChunksRef.current = [];
    const options = { mimeType: 'video/webm;codecs=vp9' };
    
    try {
      mediaRecorderRef.current = new MediaRecorder(cameraStream, options);
    } catch (e) {
      try {
        mediaRecorderRef.current = new MediaRecorder(cameraStream, { mimeType: 'video/webm' });
      } catch (e2) {
        mediaRecorderRef.current = new MediaRecorder(cameraStream);
      }
    }
    
    mediaRecorderRef.current.ondataavailable = (event) => {
      if (event.data.size > 0) {
        recordedChunksRef.current.push(event.data);
      }
    };
    
    mediaRecorderRef.current.onstop = () => {
      const blob = new Blob(recordedChunksRef.current, { type: 'video/webm' });
      const file = new File([blob], `video_${Date.now()}.webm`, { type: 'video/webm' });
      const wrappedFile = {
        id: generateFileId(),
        file: file,
        previewUrl: URL.createObjectURL(file)
      };
      setFiles(prev => [...prev, wrappedFile]);
      toast.success('Vidéo enregistrée !');
      if (!title) {
        setTitle(`Vidéo du ${new Date().toLocaleDateString('fr-FR')}`);
      }
    };
    
    mediaRecorderRef.current.start();
    setIsRecording(true);
    setRecordingTime(0);
    
    // Start timer
    recordingIntervalRef.current = setInterval(() => {
      setRecordingTime(prev => prev + 1);
    }, 1000);
  };
  
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
        recordingIntervalRef.current = null;
      }
    }
  };
  
  const formatRecordingTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  
  const capturePhoto = useCallback(() => {
    const video = videoPreviewRef.current;
    
    if (!video) {
      toast.error('Caméra non initialisée');
      return;
    }
    
    // Get dimensions
    const videoWidth = video.videoWidth || video.clientWidth || 640;
    const videoHeight = video.videoHeight || video.clientHeight || 480;
    
    if (videoWidth === 0 || videoHeight === 0) {
      toast.error('La caméra n\'est pas encore prête');
      return;
    }
    
    // Flash effect
    setCameraFlash(true);
    setTimeout(() => setCameraFlash(false), 200);
    
    // Calculate cropped area based on zoom level
    // zoom 1 = full image, zoom 2 = center 50%, zoom 3 = center 33%, etc.
    const cropWidth = videoWidth / cameraZoom;
    const cropHeight = videoHeight / cameraZoom;
    const cropX = (videoWidth - cropWidth) / 2;
    const cropY = (videoHeight - cropHeight) / 2;
    
    // Create canvas with cropped dimensions (output same size as original for quality)
    const canvas = document.createElement('canvas');
    canvas.width = videoWidth;
    canvas.height = videoHeight;
    
    const ctx = canvas.getContext('2d');
    
    // Draw cropped and scaled image
    ctx.drawImage(
      video,
      cropX, cropY, cropWidth, cropHeight,  // Source rectangle (cropped area)
      0, 0, videoWidth, videoHeight          // Destination rectangle (full canvas)
    );
    
    // Convert to blob
    canvas.toBlob(
      (blob) => {
        if (blob) {
          const fileName = `photo_${Date.now()}.jpg`;
          const file = new File([blob], fileName, { type: 'image/jpeg' });
          const wrappedFile = {
            id: generateFileId(),
            file: file,
            previewUrl: URL.createObjectURL(file)
          };
          setFiles(prev => [...prev, wrappedFile]);
          toast.success('Photo capturée !');
          
          if (!title) {
            setTitle(`Photo du ${new Date().toLocaleDateString('fr-FR')}`);
          }
        } else {
          toast.error('Échec de la capture');
        }
      },
      'image/jpeg',
      0.92
    );
  }, [title, cameraZoom]);
  
  const handleUpload = async () => {
    if (files.length === 0) {
      toast.error('Sélectionnez au moins un fichier');
      return;
    }
    
    setUploading(true);
    setProgress(0);
    setUploadedCount(0);
    
    const totalFiles = files.length;
    let successCount = 0;
    
    // Build automatic tags based on context
    let horseIds = [];
    let activityIds = [];
    let cavalierIds = [];
    
    if (context) {
      switch (context.type) {
        case 'activity':
          // Rule 1: Activity + enrolled cavaliers + instructor
          if (context.activityId) activityIds = [context.activityId];
          if (context.cavalierIds) cavalierIds = context.cavalierIds;
          if (context.instructorId) cavalierIds = [...cavalierIds, context.instructorId];
          break;
        case 'horse':
          // Rule 2: Horse only
          if (context.horseId) horseIds = [context.horseId];
          break;
        case 'cavalier':
          // Rule 3: Cavalier only
          if (context.cavalierId) cavalierIds = [context.cavalierId];
          break;
        default:
          // From gallery - no auto tags
          break;
      }
    }
    
    for (let i = 0; i < files.length; i++) {
      const wrappedFile = files[i];
      const file = wrappedFile.file; // Get actual File object
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', files.length === 1 ? (title || file.name) : `${title || 'Photo'} (${i + 1})`);
      if (description) formData.append('description', description);
      
      // Add automatic tags
      if (horseIds.length > 0) {
        formData.append('horse_ids', horseIds.join(','));
      }
      if (activityIds.length > 0) {
        formData.append('activity_ids', activityIds.join(','));
      }
      if (cavalierIds.length > 0) {
        formData.append('cavalier_ids', cavalierIds.join(','));
      }
      
      try {
        // Use fast upload endpoint for async processing
        const response = await axios.post(`${API_URL}/api/gallery/upload/fast`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            const fileProgress = (progressEvent.loaded / progressEvent.total) * 100;
            const overallProgress = ((i + fileProgress / 100) / totalFiles) * 100;
            setProgress(Math.round(overallProgress));
          }
        });
        successCount++;
        setUploadedCount(successCount);
        
        // Media will be processed in background
        // Status will be 'uploading' -> 'processing' -> 'ready'
        console.log(`Upload fast: ${file.name} - status: ${response.data.status}`);
      } catch (error) {
        console.error(`Error uploading ${file.name}:`, error);
        toast.error(`Erreur: ${file.name}`);
      }
    }
    
    setUploading(false);
    
    if (successCount > 0) {
      toast.success(`${successCount}/${totalFiles} média(s) uploadé(s)`);
      resetForm();
      onSuccess();
    }
  };
  
  const resetForm = () => {
    // Revoke all preview URLs to prevent memory leaks
    files.forEach(f => {
      if (f.previewUrl) URL.revokeObjectURL(f.previewUrl);
    });
    setFiles([]);
    setTitle('');
    setDescription('');
    setProgress(0);
    setUploadedCount(0);
    stopCamera();
  };
  
  // Preview navigation functions
  const goToPreviousFile = () => {
    if (previewIndex > 0) {
      setPreviewIndex(previewIndex - 1);
    }
  };
  
  const goToNextFile = () => {
    if (previewIndex < files.length - 1) {
      setPreviewIndex(previewIndex + 1);
    }
  };
  
  const closePreview = () => {
    setPreviewIndex(null);
  };
  
  return (
    <>
    {/* File Preview Full Screen */}
    {previewIndex !== null && files[previewIndex] && createPortal(
      <div 
        className="fixed top-0 left-0 w-screen h-screen bg-black flex flex-col items-center justify-center"
        style={{ zIndex: 100001 }}
      >
        {/* Close button */}
        <button
          type="button"
          onClick={closePreview}
          className="absolute top-4 right-4 w-12 h-12 rounded-full bg-black/50 flex items-center justify-center z-10"
        >
          <X className="w-8 h-8 text-white" />
        </button>
        
        {/* Counter */}
        <div className="absolute top-4 left-4 bg-black/50 px-3 py-1 rounded-full text-white text-sm z-10">
          {previewIndex + 1} / {files.length}
        </div>
        
        {/* Previous button */}
        {previewIndex > 0 && (
          <button
            type="button"
            onClick={goToPreviousFile}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/50 flex items-center justify-center z-10"
          >
            <ChevronLeft className="w-8 h-8 text-white" />
          </button>
        )}
        
        {/* Next button */}
        {previewIndex < files.length - 1 && (
          <button
            type="button"
            onClick={goToNextFile}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/50 flex items-center justify-center z-10"
          >
            <ChevronRight className="w-8 h-8 text-white" />
          </button>
        )}
        
        {/* Media display */}
        <div className="w-full h-full flex items-center justify-center p-4">
          {files[previewIndex].file.type.startsWith('image/') ? (
            <img 
              src={files[previewIndex].previewUrl} 
              alt=""
              className="max-w-full max-h-full object-contain"
            />
          ) : (
            <video 
              src={files[previewIndex].previewUrl}
              className="max-w-full max-h-full object-contain"
              controls
              autoPlay
              playsInline
            />
          )}
        </div>
        
        {/* Delete button */}
        <button
          type="button"
          onClick={() => {
            const fileId = files[previewIndex].id;
            removeFile(fileId);
            if (files.length <= 1) {
              closePreview();
            } else if (previewIndex >= files.length - 1) {
              setPreviewIndex(previewIndex - 1);
            }
          }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full bg-red-500 text-white flex items-center gap-2"
        >
          <Trash2 className="w-5 h-5" />
          Supprimer
        </button>
      </div>,
      document.body
    )}
    
    {/* Camera Full Screen - Rendered via Portal directly in body */}
    {useCamera && createPortal(
      <div 
        className="fixed top-0 left-0 w-screen h-screen bg-black flex flex-col"
        style={{ zIndex: 99999, paddingTop: 'env(safe-area-inset-top, 20px)' }}
      >
        {/* Flash effect */}
        {cameraFlash && (
          <div 
            className="absolute inset-0 bg-white pointer-events-none" 
            style={{ zIndex: 100000 }}
          />
        )}
        
        {/* Recording indicator */}
        {isRecording && (
          <div className="absolute top-12 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-black/60 px-4 py-2 rounded-full" style={{ zIndex: 100001 }}>
            <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
            <span className="text-white font-mono text-lg">{formatRecordingTime(recordingTime)}</span>
          </div>
        )}
        
        {/* Video preview */}
        <div className="flex-1 relative overflow-hidden bg-black">
          <video 
            ref={setVideoRef}
            autoPlay
            playsInline
            muted={cameraMode === 'photo'}
            className="absolute inset-0 w-full h-full object-cover"
            style={{ 
              transform: facingMode === 'user' ? 'scaleX(-1)' : 'none'
            }}  
            onCanPlay={(e) => e.target.play().catch(() => {})}
          />
          
          {/* Loading */}
          {!cameraStream && (
            <div className="absolute inset-0 flex items-center justify-center bg-black">
              <Loader2 className="w-10 h-10 animate-spin text-white" />
            </div>
          )}
          
          {/* Counter badge */}
          {files.length > 0 && (
            <div className="absolute top-4 left-4 bg-green-500 text-white w-10 h-10 rounded-full flex items-center justify-center font-bold shadow-lg">
              {files.length}
            </div>
          )}
          
          {/* Zoom buttons - Dynamic based on camera capabilities */}
          {zoomOptions.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-black/50 rounded-full px-2 py-1">
              {zoomOptions.map((zoom) => (
                <button
                  key={zoom}
                  type="button"
                  onClick={() => {
                    console.log('Zoom:', zoom);
                    applyZoom(zoom);
                  }}
                  className={`w-11 h-11 rounded-full flex items-center justify-center text-base font-semibold ${
                    cameraZoom === zoom ? 'bg-yellow-500 text-black' : 'text-white'
                  }`}
                >
                  {zoom < 1 ? `.${zoom * 10}` : `${zoom}×`}
                </button>
              ))}
            </div>
          )}
        </div>
        
        {/* Controls */}
        <div className="bg-black px-6 pt-4" style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 20px), 2rem)' }}>
          {/* Photo / Video toggle */}
          <div className="flex justify-center mb-6">
            <button
              type="button"
              onClick={() => {
                console.log('Mode: photo');
                setCameraMode('photo');
              }}
              className={`px-5 py-2 text-sm font-bold ${cameraMode === 'photo' ? 'text-yellow-400' : 'text-white/50'}`}
            >
              PHOTO
            </button>
            <button
              type="button"
              onClick={() => {
                console.log('Mode: video');
                setCameraMode('video');
              }}
              className={`px-5 py-2 text-sm font-bold ${cameraMode === 'video' ? 'text-yellow-400' : 'text-white/50'}`}
            >
              VIDÉO
            </button>
          </div>
          
          {/* Main buttons row */}
          <div className="flex items-center justify-around">
            {/* Close */}
            <button
              type="button"
              onClick={() => {
                console.log('Close');
                stopCamera();
              }}
              className="w-14 h-14 rounded-full bg-zinc-800 flex items-center justify-center active:bg-zinc-600"
            >
              <X className="w-8 h-8 text-white" />
            </button>
            
            {/* Capture */}
            {cameraMode === 'photo' ? (
              <button
                type="button"
                onClick={() => {
                  console.log('Capture');
                  capturePhoto();
                }}
                className="w-20 h-20 rounded-full bg-white flex items-center justify-center ring-4 ring-white/30 active:scale-90"
              >
                <div className="w-16 h-16 rounded-full bg-white border-2 border-gray-200" />
              </button>
            ) : (
              <button
                type="button"
                onClick={() => {
                  console.log('Record:', isRecording ? 'stop' : 'start');
                  isRecording ? stopRecording() : startRecording();
                }}
                className={`w-20 h-20 rounded-full flex items-center justify-center ring-4 ring-white/30 active:scale-90 ${
                  isRecording ? 'bg-red-600' : 'bg-red-500'
                }`}
              >
                {isRecording ? (
                  <div className="w-7 h-7 rounded bg-white" />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-red-600" />
                )}
              </button>
            )}
            
            {/* Switch camera */}
            <button
              type="button"
              onClick={() => {
                console.log('Switch');
                switchCamera();
              }}
              className="w-14 h-14 rounded-full bg-zinc-800 flex items-center justify-center active:bg-zinc-600"
            >
              <RefreshCw className="w-7 h-7 text-white" />
            </button>
          </div>
          
          {/* Thumbnails */}
          {files.length > 0 && (
            <div className="flex justify-center gap-2 mt-6">
              {files.slice(-3).map((wrappedFile, i) => {
                const actualIndex = files.length - 3 + i < 0 ? i : files.length - 3 + i;
                return (
                  <button
                    key={wrappedFile.id}
                    type="button"
                    onClick={() => setPreviewIndex(actualIndex >= 0 ? actualIndex : i)}
                    className="w-12 h-12 rounded-lg overflow-hidden border border-white/40 relative"
                  >
                    {wrappedFile.file.type.startsWith('image/') ? (
                      <img src={wrappedFile.previewUrl} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <>
                        <video 
                          src={wrappedFile.previewUrl} 
                          className="w-full h-full object-cover"
                          muted
                          preload="metadata"
                        />
                        <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                          <Play className="w-4 h-4 text-white fill-white" />
                        </div>
                      </>
                    )}
                  </button>
                );
              })}
              <button
                type="button"
                onClick={() => {
                  console.log('Done');
                  stopCamera();
                }}
                className="w-12 h-12 rounded-lg bg-green-500 flex items-center justify-center text-white font-bold active:bg-green-600"
              >
                OK
              </button>
            </div>
          )}
        </div>
      </div>,
      document.body
    )}
    
    <Dialog open={open && !useCamera} onOpenChange={() => { resetForm(); onClose(); }}>
      <DialogContent className="w-[95vw] max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif flex items-center gap-2">
            <FileImage className="w-5 h-5" />
            Ajouter des médias
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 mt-4">
          {/* File selection */}
          <div>
            <Label className="form-label">Fichiers</Label>
            
            <div className="mt-2">
              <Button
                variant="outline"
                className="w-full h-24 flex flex-col items-center justify-center gap-2 border-dashed border-2"
                onClick={() => fileInputRef.current?.click()}
                data-testid="select-file-btn"
              >
                <Upload className="w-6 h-6 text-stone-400" />
                <span className="text-sm text-stone-600">Choisir des fichiers</span>
              </Button>
              
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept="image/*,video/*"
                multiple
                onChange={handleFilesChange}
              />
            </div>
            
            {/* Selected files preview */}
            {files.length > 0 && (
              <div className="mt-4">
                <p className="text-sm font-medium mb-2">{files.length} fichier(s) sélectionné(s)</p>
                <div className="grid grid-cols-4 gap-2">
                  {files.map((wrappedFile) => (
                    <div key={wrappedFile.id} className="relative aspect-square bg-stone-200 rounded-lg overflow-hidden group">
                      {wrappedFile.file.type.startsWith('image/') ? (
                        <img 
                          src={wrappedFile.previewUrl} 
                          alt={wrappedFile.file.name}
                          className="w-full h-full object-cover"
                        />
                      ) : wrappedFile.file.type.startsWith('video/') ? (
                        <div className="relative w-full h-full">
                          <video 
                            src={wrappedFile.previewUrl}
                            className="w-full h-full object-cover"
                            muted
                            playsInline
                            preload="metadata"
                          />
                          <div className="absolute bottom-1 left-1">
                            <Play className="w-4 h-4 text-white drop-shadow-md fill-white" />
                          </div>
                        </div>
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Video className="w-8 h-8 text-stone-500" />
                        </div>
                      )}
                      <Button
                        variant="destructive"
                        size="sm"
                        className="absolute top-1 right-1 h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => removeFile(wrappedFile.id)}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          {/* Title and description */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="form-label">Titre</Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Titre du média"
                className="input-field"
                data-testid="media-title"
              />
            </div>
            <div>
              <Label className="form-label">Description (optionnel)</Label>
              <Input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Description..."
                className="input-field"
              />
            </div>
          </div>
          
          {/* Context info - show what will be auto-tagged */}
          {context && context.type !== 'gallery' && (
            <div className="bg-stone-50 rounded-lg p-3 text-sm text-stone-600">
              <p className="font-medium text-stone-700 mb-1">Tags automatiques :</p>
              {context.type === 'activity' && (
                <p>📅 Cette activité + {context.cavalierIds?.length || 0} cavaliers inscrits</p>
              )}
              {context.type === 'horse' && (
                <p>🐴 Ce cheval</p>
              )}
              {context.type === 'cavalier' && (
                <p>👤 Ce cavalier</p>
              )}
            </div>
          )}
          
          {/* Progress bar */}
          {uploading && (
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span>Upload en cours...</span>
                <span>{uploadedCount}/{files.length}</span>
              </div>
              <div className="w-full bg-stone-200 rounded-full h-2">
                <div 
                  className="bg-[#2E4A3D] h-2 rounded-full transition-all"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}
          
          {/* Submit button */}
          <Button 
            className="btn-primary w-full" 
            onClick={handleUpload}
            disabled={files.length === 0 || uploading}
            data-testid="upload-submit-btn"
          >
            {uploading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Upload en cours... {progress}%
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Uploader {files.length > 0 ? `(${files.length} fichier${files.length > 1 ? 's' : ''})` : ''}
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
};

// ============ Edit Media Modal ============
// ============ Edit Media Modal (Title + Tags only) ============
const EditMediaModal = ({ open, onClose, media, horses, activities, onSuccess }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [cavaliers, setCavaliers] = useState([]);
  const [saving, setSaving] = useState(false);
  
  // Fetch cavaliers list
  useEffect(() => {
    if (open) {
      fetchCavaliers();
    }
  }, [open]);
  
  // Initialize form from media
  useEffect(() => {
    if (media) {
      setTitle(media.title || '');
      setDescription(media.description || '');
      // Initialize tags
      const tags = [];
      media.horses?.forEach(h => tags.push({ type: 'horse', id: h.id, name: h.nom }));
      media.activities?.forEach(a => tags.push({ 
        type: 'activity', 
        id: a.id, 
        name: `${a.type_cours} - ${formatActivityDate(a.date_heure_debut)}`
      }));
      media.cavaliers?.forEach(c => tags.push({ 
        type: 'cavalier', 
        id: c.id, 
        name: `${c.prenom} ${c.nom}`
      }));
      setSelectedTags(tags);
    }
  }, [media]);
  
  const fetchCavaliers = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/users`);
      const cavaliersList = res.data
        .filter(u => u.role_club?.startsWith('cavalier'))
        .sort((a, b) => `${a.prenom} ${a.nom}`.localeCompare(`${b.prenom} ${b.nom}`));
      setCavaliers(cavaliersList);
    } catch (error) {
      console.error('Error fetching cavaliers:', error);
    }
  };
  
  // Sort lists
  const sortedHorses = [...(horses || [])].sort((a, b) => 
    (a.nom || '').localeCompare(b.nom || '')
  );
  const sortedActivities = [...(activities || [])].sort((a, b) => 
    new Date(b.date_heure_debut) - new Date(a.date_heure_debut)
  );
  const sortedCavaliers = cavaliers;
  
  const toggleTag = (type, id, name) => {
    const exists = selectedTags.find(t => t.type === type && t.id === id);
    if (exists) {
      setSelectedTags(selectedTags.filter(t => !(t.type === type && t.id === id)));
    } else {
      setSelectedTags([...selectedTags, { type, id, name }]);
    }
  };
  
  const handleSave = async () => {
    setSaving(true);
    try {
      const horseIds = selectedTags.filter(t => t.type === 'horse').map(t => t.id);
      const activityIds = selectedTags.filter(t => t.type === 'activity').map(t => t.id);
      const cavalierIds = selectedTags.filter(t => t.type === 'cavalier').map(t => t.id);
      
      await axios.put(`${API_URL}/api/gallery/${media.id}`, {
        title,
        description,
        horse_ids: horseIds,
        activity_ids: activityIds,
        cavalier_ids: cavalierIds
      });
      
      toast.success('Modifications enregistrées');
      onSuccess();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };
  
  if (!media) return null;
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-base font-medium flex items-center gap-2">
            <Edit2 className="w-4 h-4" />
            Modifier le média
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 mt-2">
          {/* Title */}
          <div>
            <Label className="text-xs text-stone-500">Titre</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Titre du média"
              className="mt-1"
              data-testid="edit-media-title"
            />
          </div>
          
          {/* Description */}
          <div>
            <Label className="text-xs text-stone-500">Description</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Description (optionnel)"
              className="mt-1 min-h-[60px]"
              data-testid="edit-media-description"
            />
          </div>
          
          {/* Tags section */}
          <div>
            <Label className="text-xs text-stone-500 mb-2 block">Tags</Label>
            
            {/* Selected tags */}
            {selectedTags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-3">
                {selectedTags.map((tag, i) => (
                  <Badge 
                    key={`${tag.type}-${tag.id}-${i}`}
                    variant="secondary"
                    className={`text-xs cursor-pointer hover:bg-red-100 ${
                      tag.type === 'horse' ? 'bg-amber-50 text-amber-700' :
                      tag.type === 'activity' ? 'bg-blue-50 text-blue-700' :
                      'bg-purple-50 text-purple-700'
                    }`}
                    onClick={() => toggleTag(tag.type, tag.id, tag.name)}
                  >
                    {tag.type === 'horse' ? '🐴' : tag.type === 'activity' ? '📅' : '👤'} {tag.name}
                    <X className="w-3 h-3 ml-1" />
                  </Badge>
                ))}
              </div>
            )}
            
            {/* Combined dropdown with 3 sections */}
            <div className="border rounded-lg max-h-[200px] overflow-y-auto">
              {/* Horses section */}
              <div className="border-b">
                <div className="px-3 py-1.5 bg-amber-50 text-xs font-medium text-amber-700 sticky top-0 flex items-center gap-1">
                  🐴 Chevaux
                </div>
                {sortedHorses.map(horse => {
                  const isSelected = selectedTags.some(t => t.type === 'horse' && t.id === horse.id);
                  return (
                    <div
                      key={horse.id}
                      onClick={() => toggleTag('horse', horse.id, horse.nom)}
                      className={`px-3 py-1.5 flex items-center cursor-pointer hover:bg-stone-50 ${
                        isSelected ? 'bg-green-50' : ''
                      }`}
                    >
                      <span className="flex-1 text-sm">{horse.nom}</span>
                      {isSelected && <Check className="w-4 h-4 text-green-600" />}
                    </div>
                  );
                })}
                {sortedHorses.length === 0 && (
                  <div className="px-3 py-2 text-sm text-stone-400">Aucun cheval</div>
                )}
              </div>
              
              {/* Activities section */}
              <div className="border-b">
                <div className="px-3 py-1.5 bg-blue-50 text-xs font-medium text-blue-700 sticky top-0 flex items-center gap-1">
                  📅 Activités
                </div>
                {sortedActivities.slice(0, 20).map(activity => {
                  const isSelected = selectedTags.some(t => t.type === 'activity' && t.id === activity.id);
                  const displayName = `${activity.type_cours} - ${formatActivityDate(activity.date_heure_debut)}`;
                  return (
                    <div
                      key={activity.id}
                      onClick={() => toggleTag('activity', activity.id, displayName)}
                      className={`px-3 py-1.5 flex items-center cursor-pointer hover:bg-stone-50 ${
                        isSelected ? 'bg-green-50' : ''
                      }`}
                    >
                      <span 
                        className="w-2 h-2 rounded-full mr-2 flex-shrink-0"
                        style={{ backgroundColor: activity.color || '#2E4A3D' }}
                      />
                      <span className="flex-1 text-sm truncate">{displayName}</span>
                      {isSelected && <Check className="w-4 h-4 text-green-600 flex-shrink-0" />}
                    </div>
                  );
                })}
                {sortedActivities.length === 0 && (
                  <div className="px-3 py-2 text-sm text-stone-400">Aucune activité</div>
                )}
              </div>
              
              {/* Cavaliers section */}
              <div>
                <div className="px-3 py-1.5 bg-purple-50 text-xs font-medium text-purple-700 sticky top-0 flex items-center gap-1">
                  👤 Cavaliers
                </div>
                {sortedCavaliers.map(cavalier => {
                  const isSelected = selectedTags.some(t => t.type === 'cavalier' && t.id === cavalier.id);
                  const displayName = `${cavalier.prenom} ${cavalier.nom}`;
                  return (
                    <div
                      key={cavalier.id}
                      onClick={() => toggleTag('cavalier', cavalier.id, displayName)}
                      className={`px-3 py-1.5 flex items-center cursor-pointer hover:bg-stone-50 ${
                        isSelected ? 'bg-green-50' : ''
                      }`}
                    >
                      <span className="flex-1 text-sm">{displayName}</span>
                      {isSelected && <Check className="w-4 h-4 text-green-600" />}
                    </div>
                  );
                })}
                {sortedCavaliers.length === 0 && (
                  <div className="px-3 py-2 text-sm text-stone-400">Aucun cavalier</div>
                )}
              </div>
            </div>
          </div>
          
          {/* Action buttons */}
          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              className="flex-1"
              onClick={onClose}
              disabled={saving}
            >
              Annuler
            </Button>
            <Button
              className="flex-1 bg-[#2E4A3D] hover:bg-[#1e3228]"
              onClick={handleSave}
              disabled={saving}
              data-testid="save-edit-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enregistrer'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ============ Image Settings Modal (Full Screen) ============
const ImageSettingsModal = ({ open, onClose, media, onSuccess }) => {
  const [applying, setApplying] = useState(false);
  const imgRef = useRef(null);
  
  // Pending rotation (preview only, applied on OK)
  const [pendingRotation, setPendingRotation] = useState(0);
  
  // Crop state - using react-image-crop format
  const [cropMode, setCropMode] = useState(false);
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState(null);
  const [pendingCrop, setPendingCrop] = useState(null);

  // Reset state when media changes or modal opens
  useEffect(() => {
    if (media && open) {
      setCropMode(false);
      setCrop(undefined);
      setCompletedCrop(null);
      setPendingCrop(null);
      setPendingRotation(0);
    }
  }, [media, open]);
  
  // Rotate preview (local state only)
  const handleRotate = (direction) => {
    setPendingRotation(prev => {
      const delta = direction === 'cw' ? 90 : -90;
      return (prev + delta + 360) % 360;
    });
  };
  
  // Confirm crop selection
  const handleCropConfirm = () => {
    if (completedCrop && imgRef.current &&
        completedCrop.width > 5 && completedCrop.height > 5) {
      const image = imgRef.current;
      const scaleX = image.naturalWidth / (image.width || 1);
      const scaleY = image.naturalHeight / (image.height || 1);
      const imgW = image.naturalWidth;
      const imgH = image.naturalHeight;
      const x = Math.max(0, Math.round(completedCrop.x * scaleX));
      const y = Math.max(0, Math.round(completedCrop.y * scaleY));
      const w = Math.min(imgW - x, Math.round(completedCrop.width * scaleX));
      const h = Math.min(imgH - y, Math.round(completedCrop.height * scaleY));
      if (w > 5 && h > 5) setPendingCrop({ x, y, width: w, height: h });
    }
    setCropMode(false);
    setCrop(undefined);
    setCompletedCrop(null);
  };
  
  // Cancel crop
  const handleCropCancel = () => {
    setCropMode(false);
    setCrop(undefined);
    setCompletedCrop(null);
  };
  
  // Handle close
  const handleClose = () => {
    onClose();
  };
  
  // Apply all pending changes
  const handleApplyAll = async () => {
    const hasChanges = pendingRotation !== 0 || pendingCrop;

    if (!hasChanges) {
      onClose();
      return;
    }

    setApplying(true);
    try {
      if (pendingRotation !== 0) {
        await axios.post(`${API_URL}/api/gallery/${media.id}/rotate`, { angle: pendingRotation });
      }
      if (pendingCrop) {
        await axios.post(`${API_URL}/api/gallery/${media.id}/crop`, pendingCrop);
      }
      toast.success('Modifications appliquées');
      onSuccess();
    } catch (error) {
      toast.error("Erreur lors de l'application");
    } finally {
      setApplying(false);
    }
  };

  const resetAll = () => {
    setPendingRotation(0);
    setPendingCrop(null);
  };

  const hasChanges = pendingRotation !== 0 || pendingCrop;
  const cssTransformPreview = `rotate(${pendingRotation}deg)`;
  
  if (!media || !open) return null;
  
  const imageUrl = media.file_url?.startsWith('http') ? media.file_url : resolveUrl(media.file_url);

  
  // Full screen modal using portal with high z-index
  return createPortal(
    <div 
      className="fixed inset-0 bg-black flex flex-col pointer-events-auto"
      style={{ zIndex: 999999 }}
    >
      {/* Header */}
      <div 
        className="flex items-center justify-between px-4 py-3 bg-black/90 pointer-events-auto"
        style={{ paddingTop: 'max(env(safe-area-inset-top, 0px), 0.75rem)' }}
      >
        <button
          type="button"
          onClick={cropMode ? handleCropCancel : handleClose}
          className="text-white text-sm font-medium px-3 py-2 pointer-events-auto"
          disabled={applying}
        >
          Annuler
        </button>
        <span className="text-white text-sm font-medium">
          {cropMode ? 'Recadrer' : 'Réglages'}
        </span>
        <button
          type="button"
          onClick={cropMode ? handleCropConfirm : handleApplyAll}
          className="text-white text-sm font-medium px-3 py-2 pointer-events-auto"
          disabled={applying || (cropMode && !completedCrop)}
        >
          {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : 'OK'}
        </button>
      </div>
      
      {/* Main content */}
      {cropMode ? (
        /* Crop Mode - Draw rectangle on image */
        <div className="flex-1 flex flex-col items-center justify-center p-4 overflow-hidden pointer-events-auto">
          <p className="text-white/70 text-xs mb-2">Dessinez la zone à conserver</p>
          <div className="relative max-w-full max-h-full overflow-hidden">
            <ReactCrop
              crop={crop}
              onChange={(c) => setCrop(c)}
              onComplete={(c) => setCompletedCrop(c)}
              className="max-h-[60vh]"
            >
              <img
                ref={imgRef}
                src={imageUrl}
                alt=""
                className="max-w-full max-h-[60vh] object-contain"
                style={{ display: 'block' }}
              />
            </ReactCrop>
          </div>
          {completedCrop && (
            <p className="text-white/50 text-xs mt-2">
              {Math.round(completedCrop.width)} × {Math.round(completedCrop.height)} px
            </p>
          )}
          {/* Crop action buttons */}
          <div className="flex gap-3 mt-4 pointer-events-auto">
            <button
              type="button"
              onClick={handleCropCancel}
              className="flex items-center gap-2 px-5 py-2.5 bg-white/20 hover:bg-white/30 rounded-xl text-white text-sm font-medium"
            >
              <X className="w-4 h-4" />
              Annuler
            </button>
            <button
              type="button"
              onClick={handleCropConfirm}
              disabled={!completedCrop}
              className="flex items-center gap-2 px-5 py-2.5 bg-green-500 hover:bg-green-600 disabled:opacity-40 rounded-xl text-white text-sm font-medium"
            >
              <Check className="w-4 h-4" />
              Confirmer
            </button>
          </div>
        </div>
      ) : (
        /* Normal Mode */
        <>
          {/* Image preview area - takes most of the space */}
          <div className="flex-1 flex items-center justify-center p-4 overflow-hidden pointer-events-none">
            <img
              src={imageUrl}
              alt={media.title}
              className="max-w-full max-h-full object-contain transition-all duration-300"
              style={{ transform: cssTransformPreview }}
            />
          </div>
          
          {/* Pending changes indicator */}
          {hasChanges && (
            <div className="flex items-center justify-center gap-2 py-1 pointer-events-auto">
              <span className="text-xs text-amber-500">
                {pendingRotation !== 0 && `${pendingRotation}° `}
                {pendingCrop && '✓ Recadré'}
              </span>
              <button 
                type="button"
                onClick={resetAll} 
                className="text-xs text-stone-500 underline pointer-events-auto"
              >
                Réinit.
              </button>
            </div>
          )}
          
          {/* Controls panel */}
          <div 
            className="bg-stone-900/95 rounded-t-2xl px-4 pt-4 pb-2 space-y-4 pointer-events-auto"
            style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 0.5rem)' }}
          >
            {/* Rotation & Crop buttons */}
            <div className="flex items-center justify-center gap-8">
              <button
                type="button"
                onClick={() => handleRotate('ccw')}
                className="flex flex-col items-center gap-1 pointer-events-auto"
              >
                <div className="w-12 h-12 rounded-full bg-stone-800 flex items-center justify-center active:scale-95">
                  <RotateCcw className="w-6 h-6 text-white" />
                </div>
              </button>
              
              <button
                type="button"
                onClick={() => handleRotate('cw')}
                className="flex flex-col items-center gap-1 pointer-events-auto"
              >
                <div className="w-12 h-12 rounded-full bg-stone-800 flex items-center justify-center active:scale-95">
                  <RotateCw className="w-6 h-6 text-white" />
                </div>
              </button>
              
              <button
                type="button"
                onClick={() => setCropMode(true)}
                className="flex flex-col items-center gap-1 pointer-events-auto"
              >
                <div className={`w-12 h-12 rounded-full flex items-center justify-center active:scale-95 ${
                  pendingCrop ? 'bg-amber-600' : 'bg-stone-800'
                }`}>
                  <Crop className="w-6 h-6 text-white" />
                </div>
              </button>
            </div>
            
          </div>
        </>
      )}
    </div>,
    document.body
  );
};

// ============ Share Media Modal ============
const ShareMediaModal = ({ open, onClose, media }) => {
  const [shareType, setShareType] = useState('link');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [users, setUsers] = useState([]);
  const [shareLink, setShareLink] = useState('');
  const [copied, setCopied] = useState(false);
  const [sharing, setSharing] = useState(false);
  
  useEffect(() => {
    if (open && shareType === 'internal') {
      fetchUsers();
    }
  }, [open, shareType]);
  
  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/users`);
      setUsers(res.data.filter(u => u.statut === 'actif'));
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };
  
  const handleShare = async () => {
    setSharing(true);
    try {
      let payload = { share_type: shareType };
      
      if (shareType === 'email') {
        if (!email) {
          toast.error('Entrez une adresse email');
          setSharing(false);
          return;
        }
        payload.email = email;
        payload.message = message;
      } else if (shareType === 'internal') {
        if (selectedUsers.length === 0) {
          toast.error('Sélectionnez au moins un utilisateur');
          setSharing(false);
          return;
        }
        payload.user_ids = selectedUsers;
      }
      
      const res = await axios.post(`${API_URL}/api/gallery/${media.id}/share`, payload);
      
      if (shareType === 'link' && res.data.share_link) {
        setShareLink(res.data.share_link);
      }
      
      toast.success(res.data.message);
      
      if (shareType !== 'link') {
        onClose();
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur lors du partage'));
    } finally {
      setSharing(false);
    }
  };
  
  const copyLink = () => {
    navigator.clipboard.writeText(shareLink);
    setCopied(true);
    toast.success('Lien copié !');
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif flex items-center gap-2">
            <Share2 className="w-5 h-5" />
            Partager "{media.title}"
          </DialogTitle>
        </DialogHeader>
        
        <Tabs value={shareType} onValueChange={setShareType} className="mt-4">
          <TabsList className="grid grid-cols-3">
            <TabsTrigger value="link" className="flex items-center gap-1">
              <Link2 className="w-4 h-4" />
              <span className="hidden sm:inline">Lien</span>
            </TabsTrigger>
            <TabsTrigger value="email" className="flex items-center gap-1">
              <Mail className="w-4 h-4" />
              <span className="hidden sm:inline">Email</span>
            </TabsTrigger>
            <TabsTrigger value="internal" className="flex items-center gap-1">
              <Users className="w-4 h-4" />
              <span className="hidden sm:inline">Membres</span>
            </TabsTrigger>
          </TabsList>
          
          {/* Link share */}
          <TabsContent value="link" className="space-y-4 mt-4">
            {shareLink ? (
              <div className="space-y-2">
                <Label>Lien de partage</Label>
                <div className="flex gap-2">
                  <Input value={shareLink} readOnly className="flex-1 text-xs" />
                  <Button onClick={copyLink} variant="secondary">
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                <p className="text-xs text-stone-500">Ce lien permet d'accéder au média</p>
              </div>
            ) : (
              <div className="text-center py-8">
                <Link2 className="w-12 h-12 mx-auto text-stone-400 mb-4" />
                <p className="text-sm text-stone-600 mb-4">Générer un lien de partage public</p>
                <Button onClick={handleShare} disabled={sharing} className="bg-[#2E4A3D]">
                  {sharing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Link2 className="w-4 h-4 mr-2" />}
                  Générer le lien
                </Button>
              </div>
            )}
          </TabsContent>
          
          {/* Email share */}
          <TabsContent value="email" className="space-y-4 mt-4">
            <div>
              <Label className="form-label">Adresse email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="destinataire@email.com"
                data-testid="share-email-input"
              />
            </div>
            
            <div>
              <Label className="form-label">Message (optionnel)</Label>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Ajouter un message personnalisé..."
                rows={3}
              />
            </div>
            
            <Button onClick={handleShare} disabled={sharing || !email} className="w-full bg-[#2E4A3D]">
              {sharing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
              Envoyer par email
            </Button>
          </TabsContent>
          
          {/* Internal share */}
          <TabsContent value="internal" className="space-y-4 mt-4">
            <div>
              <Label className="form-label">Sélectionner les membres</Label>
              <div className="max-h-48 overflow-y-auto border rounded-lg mt-2">
                {users.map(user => (
                  <label 
                    key={user.id}
                    className="flex items-center gap-3 p-3 hover:bg-stone-50 cursor-pointer border-b last:border-b-0"
                  >
                    <input
                      type="checkbox"
                      checked={selectedUsers.includes(user.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedUsers([...selectedUsers, user.id]);
                        } else {
                          setSelectedUsers(selectedUsers.filter(id => id !== user.id));
                        }
                      }}
                      className="rounded"
                    />
                    <span className="text-sm">{user.prenom} {user.nom}</span>
                  </label>
                ))}
              </div>
              {selectedUsers.length > 0 && (
                <p className="text-xs text-stone-500 mt-2">{selectedUsers.length} membre(s) sélectionné(s)</p>
              )}
            </div>
            
            <Button onClick={handleShare} disabled={sharing || selectedUsers.length === 0} className="w-full bg-[#2E4A3D]">
              {sharing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Users className="w-4 h-4 mr-2" />}
              Partager avec les membres
            </Button>
          </TabsContent>
        </Tabs>
        
        {/* Social share buttons */}
        <div className="border-t pt-4 mt-4">
          <p className="text-xs text-stone-500 mb-3">Partager sur les réseaux sociaux</p>
          <div className="flex gap-3 justify-center">
            {/* Facebook */}
            <button 
              onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.origin + '/gallery/' + media.id)}`, '_blank')}
              className="w-12 h-12 rounded-full bg-[#1877F2] hover:bg-[#166FE5] text-white flex items-center justify-center transition-colors"
              title="Partager sur Facebook"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </button>
            
            {/* Instagram */}
            <button 
              onClick={() => {
                navigator.clipboard.writeText(window.location.origin + '/gallery/' + media.id);
                toast.success('Lien copié ! Collez-le sur Instagram');
              }}
              className="w-12 h-12 rounded-full bg-gradient-to-tr from-[#F58529] via-[#DD2A7B] to-[#8134AF] hover:opacity-90 text-white flex items-center justify-center transition-opacity"
              title="Copier le lien pour Instagram"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
            </button>
            
            {/* WhatsApp */}
            <button 
              onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(media.title + ' ' + window.location.origin + '/gallery/' + media.id)}`, '_blank')}
              className="w-12 h-12 rounded-full bg-[#25D366] hover:bg-[#20BD5A] text-white flex items-center justify-center transition-colors"
              title="Partager sur WhatsApp"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ============ Entity Gallery Component (for embedding in other pages) ============
export const EntityGallery = ({ entityType, entityId, entityName, canUpload = true }) => {
  const [medias, setMedias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [mediaToShare, setMediaToShare] = useState(null);
  
  useEffect(() => {
    fetchMedias();
  }, [entityType, entityId]);
  
  const fetchMedias = async () => {
    try {
      const endpoint = entityType === 'horse' 
        ? `${API_URL}/api/gallery/by-horse/${entityId}`
        : `${API_URL}/api/gallery/by-activity/${entityId}`;
      const res = await axios.get(endpoint);
      setMedias(res.data);
    } catch (error) {
      console.error('Error fetching entity media:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleDeleteMedia = async (mediaId) => {
    if (!window.confirm('Supprimer ce média ?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/gallery/${mediaId}`);
      toast.success('Média supprimé');
      setSelectedMedia(null);
      fetchMedias();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur lors de la suppression'));
    }
  };
  
  const openShareModal = (media) => {
    setMediaToShare(media);
    setShareModalOpen(true);
  };
  
  if (loading) {
    return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-stone-400" /></div>;
  }
  
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-[#2E4A3D] flex items-center gap-2">
          <ImageIcon className="w-5 h-5" />
          Galerie ({medias.length})
        </h3>
        {canUpload && (
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setUploadModalOpen(true)}
            data-testid="add-media-btn"
          >
            <Plus className="w-4 h-4 mr-1" />
            Ajouter
          </Button>
        )}
      </div>
      
      {medias.length === 0 ? (
        <div className="text-center py-8 bg-stone-50 rounded-lg">
          <ImageIcon className="w-10 h-10 mx-auto text-stone-300 mb-2" />
          <p className="text-sm text-stone-500">Aucun média</p>
          {canUpload && (
            <Button 
              variant="link" 
              size="sm" 
              onClick={() => setUploadModalOpen(true)}
              className="mt-2"
            >
              Ajouter une photo ou vidéo
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {medias.map(media => (
            <div 
              key={media.id}
              className="aspect-square relative rounded-lg overflow-hidden cursor-pointer group"
              onClick={() => setSelectedMedia(media)}
            >
              {media.thumbnail_url ? (
                <img 
                  src={resolveUrl(media.thumbnail_url)}
                  alt={media.title}
                  className="w-full h-full object-cover"
                />
              ) : media.media_type === 'video' ? (
                <div className="w-full h-full bg-stone-200 flex items-center justify-center">
                  <Video className="w-8 h-8 text-stone-500" />
                </div>
              ) : (
                <img 
                  src={resolveUrl(media.file_url)}
                  alt={media.title}
                  className="w-full h-full object-cover"
                />
              )}
              
              {media.media_type === 'video' && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-8 h-8 rounded-full bg-black/50 flex items-center justify-center">
                    <Play className="w-4 h-4 text-white ml-0.5" />
                  </div>
                </div>
              )}
              
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors" />
            </div>
          ))}
        </div>
      )}
      
      {/* Media Detail Modal */}
      {selectedMedia && (
        <MediaDetailModal
          media={selectedMedia}
          onClose={() => setSelectedMedia(null)}
          onShare={() => openShareModal(selectedMedia)}
          onDelete={() => handleDeleteMedia(selectedMedia.id)}
          onUpdate={(updated) => {
            setSelectedMedia(updated);
            fetchMedias();
          }}
        />
      )}
      
      {/* Upload Modal */}
      <UploadMediaModal
        open={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        onSuccess={() => {
          setUploadModalOpen(false);
          fetchMedias();
        }}
        defaultActivityId={entityType === 'activity' ? entityId : undefined}
      />
      
      {/* Share Modal */}
      {mediaToShare && (
        <ShareMediaModal
          open={shareModalOpen}
          onClose={() => {
            setShareModalOpen(false);
            setMediaToShare(null);
          }}
          media={mediaToShare}
        />
      )}
    </div>
  );
};

// Export UploadMediaModal for use in Planning
export { UploadMediaModal, EditMediaModal, ImageSettingsModal };

export default GalleryPage;
